import { ReactElement, useEffect, useMemo, useState } from 'react'
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    type MRT_Row,
    type MRT_TableOptions,
    useMaterialReactTable,
    MRT_TableInstance,
} from 'material-react-table'
import {
    Box,
    Button,
    Checkbox,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { useAppSelector } from '../../../redux/hook'
import { fetchAllDropDownsValuesForRelationsPage } from '../../../services/dtServices'
import {
    findEntityById,
    findEntityByName,
    flattenEntityTemplates,
    getEntityId,
} from '../../../utils/EntityUtils'
import { useCreateRelation } from './hooks/createRelation'
import { useDeleteRelation } from './hooks/deleteRelation'
import { useGetRelations } from './hooks/getRelation'
import { useUpdateRelation } from './hooks/updateRelation'
import { RelationDropdowns, IRelation } from '../interfaces'
import ConfirmationDialog from '../../Common/ConfirmationDialog'
import { DragIndicatorOutlined } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { usePermissions } from '../../../hooks/usePermissions'
import { UserPermissions } from '../../../constants/constants'

const Relations = (): ReactElement => {
    const { t } = useTranslation()
    const [validationErrors, setValidationErrors] = useState<
        Record<string, string | undefined>
    >({})

    const org = useAppSelector((state) => state.org.org)
    const navList = useAppSelector((state) => state.nav.navList)
    const { selectedEntity } = useAppSelector((state) => state.dtStudio)
    const { id: entityId } = selectedEntity
    const orgId = org?.id ? org?.id : ''
    const { hasPermission } = usePermissions()
    const hasWriteAccess = useMemo(() => {
        return (
            !!selectedEntity?.ownTemplate &&
            hasPermission(UserPermissions.ENTITY_WRITER)
        )
    }, [selectedEntity?.ownTemplate, hasPermission])

    const [relationPageDropdowns, setRelationPageDropdowns] =
        useState<RelationDropdowns>({} as RelationDropdowns)
    const [loadingDropDowns, setLoadingDropDowns] = useState<boolean>(false)

    const [totalEntitesList, setTotalEntitesList] = useState(navList)
    const [entityTypeDropdownValues, setEntityTypeDropdownValues] = useState<
        any[]
    >([])

    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
    const [deletedRow, setDeletedRow] = useState<MRT_Row<IRelation> | null>(
        null,
    )

    const [requiredField, setRequiredField] = useState<boolean>(false)

    const { relationType, mappings } = relationPageDropdowns

    useEffect(() => {
        setLoadingDropDowns(true)
        fetchAllDropDownsValuesForRelationsPage(orgId, entityId).then(
            (res: RelationDropdowns) => {
                setRelationPageDropdowns(res)
                setLoadingDropDowns(false)
            },
        )
    }, [org?.id, entityId])

    useEffect(() => {
        const getTotalList = () => {
            let updatedTotalEntityList = flattenEntityTemplates(navList)
            updatedTotalEntityList = updatedTotalEntityList.filter(
                (i: any) => i.id !== selectedEntity?.id,
            )
            setTotalEntitesList(updatedTotalEntityList)
        }
        getTotalList()
    }, [navList])

    useEffect(() => {
        const availableEntityTempListNames = totalEntitesList
            .map((i: any) => i.name)
            .sort((a: string, b: string) => a.localeCompare(b))
        setEntityTypeDropdownValues(availableEntityTempListNames)
    }, [totalEntitesList])

    const columns = useMemo<MRT_ColumnDef<IRelation>[]>(
        () => [
            {
                accessorKey: 'relationName',
                header: t('relationName'),
                enableEditing: (row) => {
                    return !row.original.readOnly
                },
                muiEditTextFieldProps: {
                    placeholder: t('enterRelationName'),
                    required: true,
                    error: !!validationErrors?.relationName,
                    helperText: validationErrors?.relationName,
                    //remove any previous validation errors when user focuses on the input
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            relationName: undefined,
                        }),
                    //optionally add validation checking for onBlur or onChange
                },
            },
            {
                accessorKey: 'displayName',
                header: t('displayName'),
                enableEditing: () => {
                    return true
                },
                muiEditTextFieldProps: {
                    placeholder: t('enterDisplayName'),
                    required: true,
                    error: !!validationErrors?.displayName,
                    helperText: validationErrors?.displayName,
                    //remove any previous validation errors when user focuses on the input
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            displayName: undefined,
                        }),
                    //optionally add validation checking for onBlur or onChange
                },
            },
            {
                accessorKey: 'relationType',
                header: t('type'),
                enableEditing: (row) => {
                    return !row.original.readOnly
                },
                editVariant: 'select',
                editSelectOptions: relationType,
                muiEditTextFieldProps: {
                    placeholder: t('chooseDataType'),
                    select: true,
                    error: !!validationErrors?.relationType,
                    helperText: validationErrors?.relationType,
                },
            },
            {
                id: 'childEntity',
                header: t('entity'),
                enableEditing: (row) => {
                    return !row.original.readOnly
                },
                editVariant: 'select',
                accessorFn: (originalRow) =>
                    findEntityById(totalEntitesList, originalRow.childEntity)
                        .name,
                editSelectOptions: entityTypeDropdownValues,
                muiEditTextFieldProps: {
                    placeholder: t('chooseChildEntity'),
                    select: true,
                    error: !!validationErrors?.childEntity,
                    helperText: validationErrors?.childEntity,
                },
            },
            {
                accessorKey: 'mapping',
                header: t('mapping'),
                enableEditing: (row) => {
                    return !row.original.readOnly
                },
                editVariant: 'select',
                editSelectOptions: mappings,
                muiEditTextFieldProps: {
                    // placeholder: 'Choose Data Type',
                    placeholder: 'hello world',
                    select: true,
                    error: !!validationErrors?.mapping,
                    helperText: validationErrors?.mapping,
                },
            },
            {
                accessorKey: 'required',
                header: t('mandatory'),
                enableEditing: (row) => {
                    return !row.original.readOnly
                },
                Edit: () => {
                    return (
                        <Checkbox
                            id="relation-required"
                            value={requiredField}
                            checked={requiredField}
                            onChange={() => {
                                setRequiredField(!requiredField)
                            }}
                        />
                    )
                },
                Cell: ({ row }) => <Checkbox checked={row.original.required} />,
            },
        ],
        [
            validationErrors,
            relationPageDropdowns,
            navList,
            entityTypeDropdownValues,
            requiredField,
        ],
    )

    //call CREATE hook
    const { mutateAsync: createRelation, isPending: isCreatingRelation } =
        useCreateRelation(entityId, orgId)
    //call READ hook
    const {
        data: fetchedRelations = [],
        isError: isLoadingRelationsError,
        isFetching: isFetchingRelations,
        isLoading: isLoadingRelations,
    } = useGetRelations(entityId, orgId)
    //call UPDATE hook
    const { mutateAsync: updateRelation, isPending: isUpdatingRelation } =
        useUpdateRelation(entityId, orgId)
    //call DELETE hook
    const { mutateAsync: deleteRelation, isPending: isDeletingRelation } =
        useDeleteRelation(entityId, orgId)

    //CREATE action
    const handleCreateRelation: MRT_TableOptions<IRelation>['onCreatingRowSave'] =
        async ({ values, table }) => {
            const newValidationErrors = validateRelation(values, true, t)
            if (Object.values(newValidationErrors).some((error) => error)) {
                setValidationErrors(newValidationErrors)
                return
            }
            setValidationErrors({})
            values.childEntity = findEntityByName(
                totalEntitesList,
                values.childEntity,
            ).id
            values.required = requiredField
            await createRelation(values)
            table.setCreatingRow(null) //exit creating mode
            setRequiredField(false)
        }

    //UPDATE action
    const handleSaveRelation: MRT_TableOptions<IRelation>['onEditingRowSave'] =
        async ({ values, table, row }) => {
            const newValidationErrors = validateRelation(
                values,
                !row.original.readOnly,
                t,
            )
            if (Object.values(newValidationErrors).some((error) => error)) {
                setValidationErrors(newValidationErrors)
                return
            }
            setValidationErrors({})
            values.childEntity =
                getEntityId(totalEntitesList, values.childEntity) || ''
            values.required = requiredField
            await updateRelation({
                ...values,
                id: row.original.id,
                relKey: row.original.relKey,
                sequence: row.original.sequence,
            })
            table.setEditingRow(null) //exit editing mode
        }

    //DELETE action
    const openDeleteConfirmModal = (row: MRT_Row<IRelation>) => {
        setDeletedRow(row)
        setOpenDeleteDialog(true)
    }

    const handleDeleteDialogOperation = () => {
        const deletedID = deletedRow ? deletedRow.original.id : ''
        deleteRelation(deletedID)
        setOpenDeleteDialog(false)
    }

    //Drag

    const handleRowDrag = async (
        draggingRow: MRT_Row<IRelation>,
        hoveredRow: Partial<MRT_Row<IRelation>>,
    ) => {
        const newSequnce = hoveredRow?.original?.sequence
        const newIndex = hoveredRow?.index
        const oldIndex = draggingRow.index
        if (
            newSequnce === undefined ||
            newIndex === undefined ||
            newIndex === oldIndex
        ) {
            return
        }
        const relation: IRelation = {
            ...draggingRow.original,
            sequence: newSequnce,
        }

        // queryClient.setQueryData(
        //     ['relations', entityId],
        //     (prevRelations: IRelation[]) => {
        //         console.log('prevRelations', prevRelations)
        //         const updatedRelations = [...prevRelations]
        //         if (newIndex > oldIndex) {
        //             for (let i = oldIndex + 1; i <= newIndex; i++) {
        //                 updatedRelations[i].sequence -= 1
        //             }
        //         } else {
        //             for (let i = newIndex; i < oldIndex; i++) {
        //                 updatedRelations[i].sequence += 1
        //             }
        //         }
        //         updatedRelations.splice(oldIndex, 1)
        //         updatedRelations.splice(newIndex, 0, relation)
        //         return updatedRelations
        //     },
        // )
        await updateRelation(relation)
    }

    const IconDragProps = (
        row: MRT_Row<IRelation>,
        table: MRT_TableInstance<IRelation>,
    ) => {
        return {
            onDragEnd: () => {
                const { draggingRow, hoveredRow } = table.getState()
                if (hoveredRow && draggingRow) {
                    if (
                        hoveredRow.original?.readOnly ||
                        draggingRow.original.readOnly
                    ) {
                        //We should not reorder the inhereted relations
                        return
                    }
                    handleRowDrag(draggingRow, hoveredRow)
                }
            },
            disabled: row.original?.readOnly && !hasWriteAccess,
            sx: {
                opacity: row.original?.readOnly && !hasWriteAccess ? '0.5' : '',
            },
        }
    }

    const table = useMaterialReactTable({
        columns,
        data: fetchedRelations,
        createDisplayMode: 'row', // ('modal', and 'custom' are also available)
        editDisplayMode: 'row', // ('modal', 'cell', 'table', and 'custom' are also available)
        enableEditing: true,
        positionActionsColumn: 'last',
        enableColumnActions: false,
        enableColumnFilters: false,
        enableGlobalFilter: false,
        enableRowOrdering: true,
        enableHiding: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableSorting: false,
        enableTopToolbar: false,
        enablePagination: false,
        enableStickyHeader: true,
        getRowId: (row) => row.id,
        positionCreatingRow: 'bottom',
        displayColumnDefOptions: {
            'mrt-row-actions': { header: '', size: 10 },

            'mrt-row-drag': {
                header: t('move'),
                size: 20,
                muiTableHeadCellProps: {
                    sx: { fontWeight: 'bold', textAlign: 'center' },
                },
            },
        },
        muiToolbarAlertBannerProps: isLoadingRelationsError
            ? {
                  color: 'error',
                  children: t('errorLoadingData'),
              }
            : undefined,
        muiTableBodyCellProps: {
            sx: {
                border: 'none',
                fontSize: '14px',
            },
        },
        muiTableHeadCellProps: {
            sx: {
                border: 'none',
                fontSize: '14px',
            },
        },
        muiTablePaperProps: {
            sx: {
                boxShadow: 'none',
            },
        },
        muiBottomToolbarProps: {
            sx: {
                boxShadow: 'none',
                alignItems: 'center',
            },
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: `calc(100vh - ${hasWriteAccess ? '420px' : '370px'})`,
            },
        },
        icons: {
            DragHandleIcon: () => <DragIndicatorOutlined id="dragIcon" />,
        },
        muiRowDragHandleProps: (props: {
            row: MRT_Row<IRelation>
            table: MRT_TableInstance<IRelation>
        }) => IconDragProps(props.row, props.table),
        onCreatingRowCancel: () => {
            setValidationErrors({})
            setRequiredField(false)
        },
        onCreatingRowSave: handleCreateRelation,
        onEditingRowCancel: () => {
            setValidationErrors({})
            setRequiredField(false)
        },
        onEditingRowSave: handleSaveRelation,
        renderRowActions: ({ row, table }) =>
            hasWriteAccess ? (
                <Box sx={{ display: 'flex', gap: '1rem' }}>
                    <Tooltip title={t('edit')}>
                        <IconButton
                            id={row.original.relationName + 'edit'}
                            onClick={() => {
                                table.setEditingRow(row)
                                setRequiredField(row.original.required)
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    {row.original.readOnly ? null : (
                        <Tooltip title={t('delete')}>
                            <IconButton
                                id={row.original.relationName + 'delete'}
                                color="error"
                                onClick={() => openDeleteConfirmModal(row)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ) : null,
        enableBottomToolbar: hasWriteAccess,
        renderBottomToolbarCustomActions: ({ table }) => (
            <Button
                variant="contained"
                disabled={
                    !!table.getState().editingRow ||
                    !!table.getState().creatingRow
                }
                onClick={() => {
                    table.setCreatingRow(true)
                }}
                id="add-new-relation"
            >
                {t('addNewRelation')}
            </Button>
        ),
        state: {
            isLoading: isLoadingRelations || loadingDropDowns,
            isSaving:
                isCreatingRelation || isUpdatingRelation || isDeletingRelation,
            showAlertBanner: isLoadingRelationsError,
            showProgressBars: isFetchingRelations,
            density: 'compact',
        },
        renderEmptyRowsFallback: () => (
            <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
                {t('noRecordsToDisplay')}
            </Typography>
        ),
    })

    return (
        <>
            <MaterialReactTable table={table} />
            <ConfirmationDialog
                openDialog={openDeleteDialog}
                handleComfirm={() => handleDeleteDialogOperation()}
                handleClose={() => setOpenDeleteDialog(false)}
                DialogHeaderText={t('confirmDelete')}
                DialogBodyText={t('confirmDeleteRelation')}
            />
        </>
    )
}

export default Relations

const validateRequired = (value: string) => !!value?.length

const validateRelation = (
    relation: IRelation,
    ChildEntityCheck: boolean,
    t: (key: string) => string,
) => {
    return {
        relationName: !validateRequired(relation.relationName)
            ? t('relationNameRequired')
            : '',
        displayName: !validateRequired(relation.displayName)
            ? t('displayNameRequired')
            : '',
        relationType: !validateRequired(relation.relationType)
            ? t('relationTypeRequired')
            : '',
        childEntity:
            !validateRequired(
                relation.childEntity === undefined ? '' : relation.childEntity,
            ) && ChildEntityCheck
                ? t('childEntityRequired')
                : '',
        mapping: !validateRequired(relation.mapping)
            ? t('mappingRequired')
            : '',
    }
}
